
import MeetHourJS, { browser } from '../lib-meet-hour';
import { updateSettings } from '../settings';
import { parseURLParams } from '../util';

import logger from './logger';

declare var APP: Object;

const webrtcKindToMHKindTranslator = {
    audioinput: 'audioInput',
    audiooutput: 'audioOutput',
    videoinput: 'videoInput'
};

/**
 * Detects the use case when the labels are not available if the A/V permissions
 * are not yet granted.
 *
 * @param {Object} state - The redux state.
 * @returns {boolean} - True if the labels are already initialized and false
 * otherwise.
 */
export function areDeviceLabelsInitialized(state: Object) {
    // TODO: Replace with something that doesn't use APP when the conference.js logic is reactified.
    if (APP.conference._localTracksInitialized) {
        return true;
    }

    for (const type of [ 'audioInput', 'audioOutput', 'videoInput' ]) {
        if ((state['features/base/devices'].availableDevices[type] || []).find(d => Boolean(d.label))) {
            return true;
        }
    }

    return false;
}

/**
 * Get device id of the audio output device which is currently in use.
 * Empty string stands for default device.
 *
 * @returns {string}
 */
export function getAudioOutputDeviceId() {
    return MeetHourJS.mediaDevices.getAudioOutputDevice();
}

/**
 * Finds the real device id of the default device of the given type.
 *
 * @param {Object} state - The redux state.
 * @param {*} kind - The type of the device. One of "audioInput",
 * "audioOutput", and "videoInput". Also supported is all lowercase versions
 * of the preceding types.
 * @returns {string|undefined}
 */
export function getDefaultDeviceId(state: Object, kind: string) {
    const kindToSearch = webrtcKindToMHKindTranslator[kind] || kind;
    const devices = state['features/base/devices']?.availableDevices?.[kindToSearch] || [];
    const defaultDevice = devices.find(d => d.deviceId === 'default');

    if (!defaultDevice) {
        return devices[0]?.deviceId;
    }

    // Find the device with a matching group id.
    const matchingDevice = defaultDevice.groupId
        ? devices.find(d => d.deviceId !== 'default' && d.groupId === defaultDevice.groupId)
        : undefined;

    if (matchingDevice) {
        return matchingDevice.deviceId;
    }

    return defaultDevice.deviceId;
}

/**
 * Finds a device with a label that matches the passed label and returns its id.
 *
 * @param {Object} state - The redux state.
 * @param {string} label - The label.
 * @param {string} kind - The type of the device. One of "audioInput",
 * "audioOutput", and "videoInput". Also supported is all lowercase versions
 * of the preceding types.
 * @returns {string|undefined}
 */
export function getDeviceIdByLabel(state: Object, label: string, kind: string) {
    const kindToSearch = webrtcKindToMHKindTranslator[kind] || kind;

    const device
        = (state['features/base/devices'].availableDevices[kindToSearch] || [])
            .find(d => d.label === label);

    if (device) {
        return device.deviceId;
    }
}

/**
 * Finds a device with a label that matches the passed id and returns its label.
 *
 * @param {Object} state - The redux state.
 * @param {string} id - The device id.
 * @param {string} kind - The type of the device. One of "audioInput",
 * "audioOutput", and "videoInput". Also supported is all lowercase versions
 * of the preceding types.
 * @returns {string|undefined}
 */
export function getDeviceLabelById(state: Object, id: string, kind: string) {
    const kindToSearch = webrtcKindToMHKindTranslator[kind] || kind;

    const device
        = (state['features/base/devices'].availableDevices[kindToSearch] || [])
        .find(d => d.deviceId === id);

    if (device) {
        return device.label;
    }
}

/**
 * Returns the devices set in the URL.
 *
 * @param {Object} state - The redux state.
 * @returns {Object|undefined}
 */
export function getDevicesFromURL(state: Object) {
    const urlParams
        = parseURLParams(state['features/base/connection'].locationURL);

    const audioOutput = urlParams['devices.audioOutput'];
    const videoInput = urlParams['devices.videoInput'];
    const audioInput = urlParams['devices.audioInput'];

    if (!audioOutput && !videoInput && !audioInput) {
        return undefined;
    }

    const devices = {};

    audioOutput && (devices.audioOutput = audioOutput);
    videoInput && (devices.videoInput = videoInput);
    audioInput && (devices.audioInput = audioInput);

    return devices;
}

/**
 * Converts an array of media devices into an object organized by device kind.
 *
 * @param {Array<MediaDeviceInfo>} devices - Available media devices.
 * @private
 * @returns {Object} An object with the media devices split by type. The keys
 * are device type and the values are arrays with devices matching the device
 * type.
 */
export function groupDevicesByKind(devices: Object[]): Object {
    return {
        audioInput: devices.filter(device => device.kind === 'audioinput'),
        audioOutput: devices.filter(device => device.kind === 'audiooutput'),
        videoInput: devices.filter(device => device.kind === 'videoinput')
    };
}

/**
 * Filters audio devices from a list of MediaDeviceInfo objects.
 *
 * @param {Array<MediaDeviceInfo>} devices - Unfiltered media devices.
 * @private
 * @returns {Array<MediaDeviceInfo>} Filtered audio devices.
 */
export function filterAudioDevices(devices: Object[]): Object {
    return devices.filter(device => device.kind === 'audioinput');
}

/**
 * We want to strip any device details that are not very user friendly, like usb ids put in brackets at the end.
 *
 * @param {string} label - Device label to format.
 *
 * @returns {string} - Formatted string.
 */
export function formatDeviceLabel(label: string) {

    let formattedLabel = label;

    // Remove braked description at the end as it contains non user friendly strings i.e.
    // Microsoft® LifeCam HD-3000 (045e:0779:31dg:d1231)
    const ix = formattedLabel.lastIndexOf('(');

    if (ix !== -1) {
        formattedLabel = formattedLabel.substr(0, ix);
    }

    return formattedLabel;
}

/**
 * Values derived from a device list, keyed by the list they were derived from. The settings popups are mounted for
 * the whole call around the mic and camera buttons; deriving a new array on every mapStateToProps call failed
 * react-redux's shallow comparison and re-rendered them - and the buttons inside them - on every action. The
 * device lists are replaced, never mutated, when devices change, so a list is a valid key.
 */
const _deviceDataCache = new WeakMap();
const _deviceIdsCache = new WeakMap();
const _deviceLabelsCache = new WeakMap();
const _NO_DEVICES = [];

/**
 * Returns {@code derive(list)}, computed once per list.
 *
 * @param {Array<Object>} list - The device list.
 * @param {WeakMap} cache - The cache for this kind of derived value.
 * @param {Function} derive - Computes the value from the list.
 * @returns {*}
 */
function _deriveFromDeviceList(list, cache, derive) {
    if (!list) {
        return derive(_NO_DEVICES);
    }

    let value = cache.get(list);

    if (!value) {
        value = derive(list);
        cache.set(list, value);
    }

    return value;
}

/**
 * Returns a list of objects containing all the microphone device ids and labels.
 *
 * @param {Object} state - The state of the application.
 * @returns {Object[]}
 */
export function getAudioInputDeviceData(state: Object) {
    return _deriveFromDeviceList(
        state['features/base/devices'].availableDevices.audioInput,
        _deviceDataCache,
        list => list.map(({ deviceId, label }) => {
            return {
                deviceId,
                label
            };
        }));
}

/**
 * Returns a list of objectes containing all the output device ids and labels.
 *
 * @param {Object} state - The state of the application.
 * @returns {Object[]}
 */
export function getAudioOutputDeviceData(state: Object) {
    return _deriveFromDeviceList(
        state['features/base/devices'].availableDevices.audioOutput,
        _deviceDataCache,
        list => list.map(({ deviceId, label }) => {
            return {
                deviceId,
                label
            };
        }));
}

/**
 * Returns a list of all the camera device ids.
 *
 * @param {Object} state - The state of the application.
 * @returns {string[]}
 */
export function getVideoDeviceIds(state: Object) {
    return _deriveFromDeviceList(
        state['features/base/devices'].availableDevices.videoInput,
        _deviceIdsCache,
        list => list.map(({ deviceId }) => deviceId));
}

/**
 * Returns the label of every camera keyed by its device id.
 *
 * @param {Object} state - The state of the application.
 * @returns {Object<string, string>}
 */
export function getVideoDeviceLabels(state: Object) {
    return _deriveFromDeviceList(
        state['features/base/devices'].availableDevices.videoInput || _NO_DEVICES,
        _deviceLabelsCache,
        list => {
            const labels = {};

            for (const { deviceId, label } of list) {
                labels[deviceId] = label;
            }

            return labels;
        });
}

/**
 * Returns true if there are devices of a specific type or on native platform.
 *
 * @param {Object} state - The state of the application.
 * @param {string} type - The type of device: VideoOutput | audioOutput | audioInput.
 *
 * @returns {boolean}
 */
export function hasAvailableDevices(state: Object, type: string) {
    if (state['features/base/devices'] === undefined) {
        return true;
    }

    if (type === 'videoInput') {

        return state['features/base/devices'].availableDevices[type].length > 0;
    }

    if (type === 'audioInput') {
        return state['features/base/devices'].availableDevices[type].length > 1;
    }
}

/**
 * Set device id of the audio output device which is currently in use.
 * Empty string stands for default device.
 *
 * @param {string} newId - New audio output device id.
 * @param {Function} dispatch - The Redux dispatch function.
 * @param {boolean} userSelection - Whether this is a user selection update.
 * @param {?string} newLabel - New audio output device label to store.
 * @returns {Promise}
 */
export function setAudioOutputDeviceId(
        newId: string = 'default',
        dispatch: Function,
        userSelection: boolean = false,
        newLabel: ?string): Promise<*> {

    logger.debug(`setAudioOutputDevice: ${String(newLabel)}[${newId}]`);

    // Diagnostic: a user-selected audio output device that is no longer present in the
    // current device list (USB headset unplugged, dock removed, Bluetooth speaker powered
    // off between sessions) is the most common cause of "I cannot hear anyone after
    // rejoining" on Windows. setSinkId rejects with NotFoundError, which the existing
    // handler swallows, so the failure is invisible without this log. Note: this does NOT
    // explain "no one can hear ME" (that is send-side), only "I cannot hear them" - but
    // both complaints show up together when the rejoin corrupts both sides.
    if (newId && newId !== 'default' && typeof APP !== 'undefined') {
        const state = APP.store?.getState?.();
        const known = state?.['features/base/devices']?.availableDevices?.audioOutput ?? [];
        const persistedNotPresent
            = known.length > 0 && !known.some(d => d.deviceId === newId);

        if (persistedNotPresent) {
            logger.warn(
                '[DIAG] setAudioOutputDevice: persisted audioOutput deviceId'
                + ` '${newId}' (${String(newLabel) || '<unlabeled>'}) is not in the`
                + ` current device list (${known.length} known). The user may have`
                + ' unplugged or removed this device; playback will fall back to default.');
        }
    }

    if (!MeetHourJS.mediaDevices.isDeviceChangeAvailable('output')) {
        logger.warn('Adjusting audio output is not supported');

        const newSettings = {
            audioOutputDeviceId: newId,
            userSelectedAudioOutputDeviceId: undefined,
            userSelectedAudioOutputDeviceLabel: undefined
        };

        if (userSelection) {
            newSettings.userSelectedAudioOutputDeviceId = newId;
            newSettings.userSelectedAudioOutputDeviceLabel = newLabel;
        } else {
            delete newSettings.userSelectedAudioOutputDeviceId;
            delete newSettings.userSelectedAudioOutputDeviceLabel;
        }

        return Promise.resolve(dispatch(updateSettings(newSettings)));
    }

    if (browser.isWebKitBased()) {
        const newSettings = {
            audioOutputDeviceId: newId,
            userSelectedAudioOutputDeviceId: undefined,
            userSelectedAudioOutputDeviceLabel: undefined
        };

        if (userSelection) {
            newSettings.userSelectedAudioOutputDeviceId = newId;
            newSettings.userSelectedAudioOutputDeviceLabel = newLabel;
        } else {
            delete newSettings.userSelectedAudioOutputDeviceId;
            delete newSettings.userSelectedAudioOutputDeviceLabel;
        }

        return Promise.resolve(dispatch(updateSettings(newSettings)));
    }

    return MeetHourJS.mediaDevices.setAudioOutputDevice(newId)
        .then(() => {
            const newSettings = {
                audioOutputDeviceId: newId,
                userSelectedAudioOutputDeviceId: undefined,
                userSelectedAudioOutputDeviceLabel: undefined
            };

            if (userSelection) {
                newSettings.userSelectedAudioOutputDeviceId = newId;
                newSettings.userSelectedAudioOutputDeviceLabel = newLabel;
            } else {
                // a flow workaround, I needed to add 'userSelectedAudioOutputDeviceId: undefined'
                delete newSettings.userSelectedAudioOutputDeviceId;
                delete newSettings.userSelectedAudioOutputDeviceLabel;
            }

            return dispatch(updateSettings(newSettings));
        });
}
